import { Link } from "react-router-dom";
import { MapPin, Navigation, Phone } from "lucide-react";
import OpenStatus from "@/components/OpenStatus";
import LocationName from "@/components/LocationName";
import { addressLine, isPlaceholder, isReachablePhone, mapsDirectionsUrl, safeImageUrl } from "@/lib/hours";

export default function LocationCard({ location, distanceKm = null, titleAs: TitleTag = "h3", eager = false, tone = "dark" }) {
  const { street, city } = addressLine(location);
  const hero = safeImageUrl(location.hero_image?.url);
  const bright = tone === "bright";

  return (
    <article
      className={`group flex h-full flex-col overflow-hidden rounded-xl shadow-lg transition-colors duration-300 ${
        bright
          ? "border border-[#E4E4E7] bg-white shadow-sm hover:border-[#E11D2A] hover:shadow-md"
          : "border border-[#27272A] bg-[#16161A] hover:border-[#E11D2A]/40"
      }`}
      data-testid={`location-card-${location.slug}`}
    >
      <div className={`snap-grow relative aspect-[16/9] overflow-hidden ${bright ? "bg-[#F4F4F5]" : "bg-[#1E1E24]"}`}>
        {hero && (
          <img
            src={hero}
            alt={location.hero_image?.alt || location.name}
            width="800"
            height="450"
            loading={eager ? "eager" : "lazy"}
            fetchPriority={eager ? "high" : undefined}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        )}
        <div className={`absolute inset-x-0 bottom-0 h-16 ${bright ? "bg-gradient-to-t from-white to-transparent" : "bg-gradient-to-t from-[#16161A] to-transparent"}`} aria-hidden="true" />
        {location.is_hq && (
          <span className="absolute left-3 top-3 rounded-full bg-[#0E0E10]/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#F5F5F3] backdrop-blur">
            Zentrale
          </span>
        )}
      </div>
      <div className="snap-card-body flex flex-1 flex-col gap-3 p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <TitleTag className={`font-display text-lg font-bold ${bright ? "text-[#1A1A1A]" : "text-[#F5F5F3]"}`}>
            <LocationName name={location.name} tone={bright ? "light" : "dark"} />
          </TitleTag>
          {distanceKm != null && (
            <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium ${bright ? "border-[#E4E4E7] text-[#52525B]" : "border-[#27272A] text-[#A1A1AA]"}`} data-testid={`location-card-distance-${location.slug}`}>
              {distanceKm < 1 ? `${Math.round(distanceKm * 1000)} m` : `${distanceKm.toFixed(1)} km`}
            </span>
          )}
        </div>
        <OpenStatus location={location} />
        <p className={`flex items-start gap-2 text-sm ${bright ? "text-[#52525B]" : "text-[#A1A1AA]"}`}>
          <MapPin className={`mt-0.5 h-4 w-4 shrink-0 ${bright ? "text-[#E11D2A]" : "text-[#F54855]"}`} aria-hidden="true" />
          <span>
            {isPlaceholder(street) ? <span className="italic">{street}</span> : street}
            <br />
            {isPlaceholder(city) ? <span className="italic">{city}</span> : city}
          </span>
        </p>
        {location.short_description && (
          <p className={`text-sm leading-relaxed ${bright ? "text-[#52525B]" : "text-[#A1A1AA]"}`}>{location.short_description}</p>
        )}
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
          <Link
            to={`/standorte/${location.slug}`}
            className="inline-flex min-h-[44px] items-center rounded-lg bg-[#E11D2A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#F02837]"
            data-testid={`location-card-details-${location.slug}`}
          >
            Details
          </Link>
          <a
            href={mapsDirectionsUrl(location)}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
              bright
                ? "border-[#E4E4E7] text-[#1A1A1A] hover:border-[#E11D2A]/50 hover:bg-[#F4F4F5]"
                : "border-[#27272A] text-[#F5F5F3] hover:border-[#E11D2A]/40 hover:bg-[#1E1E24]"
            }`}
            data-testid={`location-card-route-${location.slug}`}
          >
            <Navigation className="h-4 w-4" aria-hidden="true" /> Route
          </a>
          {isReachablePhone(location.phone) && (
            <a
              href={`tel:${location.phone.replace(/[\s\-/()]/g, "")}`}
              className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
                bright
                  ? "border-[#E4E4E7] text-[#1A1A1A] hover:border-[#E11D2A]/50 hover:bg-[#F4F4F5]"
                  : "border-[#27272A] text-[#F5F5F3] hover:border-[#E11D2A]/40 hover:bg-[#1E1E24]"
              }`}
              data-testid={`location-card-phone-${location.slug}`}
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> Anrufen
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
